<template>
    <v-app id="inspire">
        <v-navigation-drawer
                v-model="drawer"
                :clipped="lgAndUp"
                app
        >
            <v-list density="compact">
                <template v-for="item in items" :key="item.text || item.heading">
                    <v-row
                            v-if="item.heading"
                            align="center"
                    >
                        <v-col cols="6">
                            <v-list-subheader v-if="item.heading">
                                {{ item.heading }}
                            </v-list-subheader>
                        </v-col>
                        <v-col
                                cols="6"
                                class="text-center"
                        >
                            <a
                                    href="#"
                                    class="text-body-2 text-black"
                            >EDIT</a>
                        </v-col>
                    </v-row>
                    <v-list-group
                            v-else-if="item.children"
                            v-model="item.model"
                    >
                        <template v-slot:activator="{ props }">
                            <v-list-item
                                v-bind="props"
                                :prepend-icon="item.model ? item.icon : item['icon-alt']"
                                :title="item.text"
                            ></v-list-item>
                        </template>
                        <v-list-item
                                v-for="(child, i) in item.children"
                                :key="i"
                                :prepend-icon="child.icon"
                                :title="child.text"
                        >
                        </v-list-item>
                    </v-list-group>
                    <v-list-item
                            v-else
                            :prepend-icon="item.icon"
                            :title="item.text"
                            :class="{ 'v-list-item--active': activeSection === item.text }"
                            @click="activeSection = item.text"
                    >
                    </v-list-item>

                </template>
            </v-list>
        </v-navigation-drawer>

        <v-app-bar
                :clipped-left="lgAndUp"
                app
        >
            <v-toolbar-title
                    style="width: 300px"
                    class="ml-0 pl-4"
            >
                <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
                <span class="hidden-sm-and-down">Images</span>
            </v-toolbar-title>
            <v-select
                    v-model="selectedPropertyId"
                    :items="properties"
                    item-title="name"
                    item-value="id"
                    label="Property"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                    style="max-width: 250px"
                    class="mx-4"
                    @update:modelValue="onPropertyChanged"
            ></v-select>
            <div class="flex-grow-1"></div>
            <v-btn icon>
                <v-icon>mdi-apps</v-icon>
            </v-btn>
            <v-btn icon>
                <v-icon>mdi-bell</v-icon>
            </v-btn>
            <v-btn icon @click="logout()">
                <v-icon>mdi-logout</v-icon>
            </v-btn>
        </v-app-bar>
        <v-main>
            <v-container fluid class="pa-6">
                <!-- Images Gallery Section -->
                <template v-if="activeSection === 'Images'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Gallery Images</h2>
                        <div class="flex-grow-1"></div>
                        <v-btn
                            variant="outlined"
                            :prepend-icon="sortMode ? 'mdi-check' : 'mdi-sort'"
                            class="text-capitalize"
                            :color="sortMode ? 'green' : 'black'"
                            rounded="lg"
                            @click="toggleSortMode"
                        >
                            {{ sortMode ? 'Done sorting' : 'Sort images' }}
                        </v-btn>
                    </div>
                    <p v-if="sortMode" class="text-body-2 text-grey-darken-1 mb-4">
                        <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                        Drag and drop images to reorder. Click "Done sorting" to save.
                    </p>

                    <!-- Sort mode: draggable grid -->
                    <template v-if="sortMode">
                        <v-row>
                            <v-col
                                v-for="(item, i) in sortedAssetItems"
                                :key="item.filename"
                                cols="12" sm="6" md="4" lg="3"
                            >
                                <div
                                    class="sort-item"
                                    draggable="true"
                                    @dragstart="onDragStart(i)"
                                    @dragover="onDragOver($event)"
                                    @drop="onDrop(i)"
                                    @dragend="onDragEnd"
                                    :class="{ 'sort-item-dragging': dragIndex === i }"
                                >
                                    <div class="sort-handle d-flex align-center">
                                        <v-icon class="mr-1" size="small">mdi-drag</v-icon>
                                        <span class="text-body-2 font-weight-bold">{{ i + 1 }}</span>
                                    </div>
                                    <v-img
                                        :src="item.src"
                                        :aspect-ratio="16/10"
                                        cover
                                        class="rounded-lg"
                                    />
                                    <div class="text-caption text-grey-darken-1 mt-1 text-truncate">{{ item.filename }}</div>
                                </div>
                            </v-col>
                        </v-row>
                    </template>

                    <!-- View mode: clickable image grid -->
                    <template v-else>
                        <v-row>
                            <v-col
                                v-for="(img, i) in images"
                                :key="i"
                                cols="12" sm="6" md="4" lg="3"
                            >
                                <v-img
                                    :src="img"
                                    :aspect-ratio="16/10"
                                    cover
                                    class="rounded-lg cursor-pointer image-card"
                                    @click="openLightbox(i)"
                                />
                            </v-col>
                        </v-row>
                    </template>
                </template>

                <!-- Fees Section -->
                <template v-if="activeSection === 'Fees'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Fees</h2>
                        <div class="flex-grow-1"></div>
                        <v-btn
                            color="primary"
                            prepend-icon="mdi-content-save"
                            class="text-capitalize"
                            rounded="lg"
                            :loading="feesSaving"
                            @click="saveFees"
                        >
                            Save Fees
                        </v-btn>
                    </div>

                    <v-alert v-if="feeError" type="error" closable class="mb-4" @click:close="feeError = ''">
                        {{ feeError }}
                    </v-alert>
                    <v-alert v-if="feeSuccess" type="success" closable class="mb-4" @click:close="feeSuccess = ''">
                        {{ feeSuccess }}
                    </v-alert>

                    <div v-if="feesLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <template v-else>
                        <v-card v-for="field in feeFields" :key="field.key" class="mb-4 rounded-lg pa-4" variant="outlined">
                            <div class="d-flex align-center flex-wrap" style="gap: 16px;">
                                <div style="min-width: 200px;">
                                    <div class="text-subtitle-1 font-weight-medium">{{ field.label }}</div>
                                    <v-btn-toggle v-model="field.type" mandatory density="compact" class="mt-1" @update:modelValue="field.value = null">
                                        <v-btn value="amount" size="small" color="blue" variant="tonal">Fixed</v-btn>
                                        <v-btn value="percentage" size="small" color="orange" variant="tonal">Percentage</v-btn>
                                    </v-btn-toggle>
                                </div>
                                <v-text-field
                                    v-if="field.type === 'amount'"
                                    v-model.number="field.value"
                                    label="Amount ($)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    :placeholder="'e.g. 150.00'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    prefix="$"
                                    style="max-width: 200px;"
                                />
                                <v-text-field
                                    v-else
                                    v-model.number="field.value"
                                    label="Percentage"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    :placeholder="'e.g. 3.25'"
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details
                                    suffix="%"
                                    style="max-width: 200px;"
                                />
                            </div>
                        </v-card>
                    </template>
                </template>

                <!-- Discounts Section -->
                <template v-if="activeSection === 'Discounts'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Discounts</h2>
                        <div class="flex-grow-1"></div>
                        <v-btn
                            color="primary"
                            prepend-icon="mdi-plus"
                            class="text-capitalize"
                            rounded="lg"
                            @click="openDiscountDialog()"
                        >
                            Add Discount
                        </v-btn>
                    </div>

                    <v-alert v-if="discountError" type="error" closable class="mb-4" @click:close="discountError = ''">
                        {{ discountError }}
                    </v-alert>
                    <v-alert v-if="discountSuccess" type="success" closable class="mb-4" @click:close="discountSuccess = ''">
                        {{ discountSuccess }}
                    </v-alert>

                    <div v-if="discountsLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <template v-else-if="discounts.length > 0">
                        <v-table class="rounded-lg elevation-1">
                            <thead>
                                <tr>
                                    <th class="text-left">Type</th>
                                    <th class="text-left">Threshold (days)</th>
                                    <th class="text-left">Discount (%)</th>
                                    <th class="text-left">Description</th>
                                    <th class="text-left">Active</th>
                                    <th class="text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="discount in discounts" :key="discount.id">
                                    <td>
                                        <v-chip :color="discount.type === 'LENGTH_OF_STAY' ? 'blue' : 'orange'" size="small" label>
                                            {{ discount.type === 'LENGTH_OF_STAY' ? 'Length of Stay' : 'Last Minute' }}
                                        </v-chip>
                                    </td>
                                    <td>
                                        {{ discount.type === 'LENGTH_OF_STAY'
                                            ? discount.thresholdDays + '+ nights'
                                            : 'Within ' + discount.thresholdDays + ' days' }}
                                    </td>
                                    <td>{{ discount.percentage }}%</td>
                                    <td>{{ discount.description || '—' }}</td>
                                    <td>
                                        <v-icon :color="discount.active ? 'green' : 'grey'" size="small">
                                            {{ discount.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                        </v-icon>
                                    </td>
                                    <td class="text-right">
                                        <v-btn icon variant="text" size="small" @click="openDiscountDialog(discount)">
                                            <v-icon size="small">mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn icon variant="text" size="small" color="red" @click="confirmDeleteDiscount(discount)">
                                            <v-icon size="small">mdi-delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </v-table>
                    </template>

                    <v-card v-else class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-percent-outline</v-icon>
                        <p class="text-body-1 text-grey">No discounts configured yet.</p>
                    </v-card>

                    <!-- Discount Add/Edit Dialog -->
                    <v-dialog v-model="discountDialog" max-width="500">
                        <v-card rounded="xl" class="pa-4">
                            <v-card-title class="text-h6">
                                {{ editingDiscount.id ? 'Edit Discount' : 'Add Discount' }}
                            </v-card-title>
                            <v-card-text>
                                <v-form v-model="discountFormValid">
                                    <v-select
                                        v-model="editingDiscount.type"
                                        :items="discountTypes"
                                        item-title="title"
                                        item-value="value"
                                        label="Discount Type"
                                        variant="outlined"
                                        density="comfortable"
                                        :rules="[v => !!v || 'Type is required']"
                                        class="mb-2"
                                    />
                                    <v-text-field
                                        v-model.number="editingDiscount.thresholdDays"
                                        :label="editingDiscount.type === 'LENGTH_OF_STAY' ? 'Minimum nights for discount' : 'Days before arrival'"
                                        :hint="editingDiscount.type === 'LENGTH_OF_STAY' ? 'Discount applies when booking this many nights or more' : 'Discount applies when booking within this many days of check-in'"
                                        persistent-hint
                                        type="number"
                                        min="1"
                                        variant="outlined"
                                        density="comfortable"
                                        :rules="[v => !!v && v >= 1 || 'Must be at least 1']"
                                        class="mb-2"
                                    />
                                    <v-text-field
                                        v-model.number="editingDiscount.percentage"
                                        label="Discount Percentage"
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        max="100"
                                        suffix="%"
                                        variant="outlined"
                                        density="comfortable"
                                        :rules="[v => !!v && v > 0 && v <= 100 || 'Must be between 0 and 100']"
                                        class="mb-2"
                                    />
                                    <v-text-field
                                        v-model="editingDiscount.description"
                                        label="Description (optional)"
                                        variant="outlined"
                                        density="comfortable"
                                        class="mb-2"
                                    />
                                    <v-switch
                                        v-model="editingDiscount.active"
                                        label="Active"
                                        color="primary"
                                        hide-details
                                    />
                                </v-form>
                            </v-card-text>
                            <v-card-actions class="justify-end px-4 pb-4">
                                <v-btn variant="text" @click="discountDialog = false">Cancel</v-btn>
                                <v-btn color="primary" variant="flat" :disabled="!discountFormValid" @click="saveDiscount">
                                    {{ editingDiscount.id ? 'Update' : 'Create' }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <!-- Discount Delete Confirmation Dialog -->
                    <v-dialog v-model="discountDeleteDialog" max-width="400">
                        <v-card rounded="xl" class="pa-4">
                            <v-card-title class="text-h6">Delete Discount</v-card-title>
                            <v-card-text>Are you sure you want to delete this discount?</v-card-text>
                            <v-card-actions class="justify-end px-4 pb-4">
                                <v-btn variant="text" @click="discountDeleteDialog = false">Cancel</v-btn>
                                <v-btn color="red" variant="flat" @click="deleteDiscount">Delete</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </template>

                <!-- Reservations Section -->
                <template v-if="activeSection === 'Reservations'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Reservations</h2>
                        <div class="flex-grow-1"></div>
                        <v-btn
                            color="primary"
                            prepend-icon="mdi-plus"
                            class="text-capitalize"
                            rounded="lg"
                            @click="openReservationDialog()"
                        >
                            Add Reservation
                        </v-btn>
                    </div>

                    <v-alert v-if="reservationError" type="error" closable class="mb-4" @click:close="reservationError = ''">
                        {{ reservationError }}
                    </v-alert>

                    <v-table v-if="reservations.length > 0" class="rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left">Guest</th>
                                <th class="text-left">Check-in</th>
                                <th class="text-left">Check-out</th>
                                <th class="text-center">Nights</th>
                                <th class="text-center">Adults</th>
                                <th class="text-center">Children</th>
                                <th class="text-center">Infants</th>
                                <th class="text-center">Pets</th>
                                <th class="text-right">Discount</th>
                                <th class="text-right">Total</th>
                                <th class="text-left">Status</th>
                                <th class="text-left">PayPal Order</th>
                                <th class="text-center" style="width: 120px">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="res in reservations" :key="res.id">
                                <td>
                                    <div class="font-weight-medium">{{ res.guestName || '—' }}</div>
                                    <div class="text-caption text-grey">{{ res.guestEmail || '' }}</div>
                                </td>
                                <td>{{ res.checkIn }}</td>
                                <td>{{ res.checkOut }}</td>
                                <td class="text-center">{{ res.nights }}</td>
                                <td class="text-center">{{ res.adults }}</td>
                                <td class="text-center">{{ res.children || 0 }}</td>
                                <td class="text-center">{{ res.infants || 0 }}</td>
                                <td class="text-center">{{ res.pets || 0 }}</td>
                                <td class="text-right">{{ res.discountAmount ? formatCents(res.discountAmount) : '—' }}</td>
                                <td class="text-right font-weight-medium">{{ formatCents(res.totalAmount) }}</td>
                                <td>
                                    <v-chip
                                        :color="getStatusColor(res.status)"
                                        size="small"
                                        variant="tonal"
                                    >
                                        {{ res.status }}
                                    </v-chip>
                                </td>
                                <td class="text-caption">{{ res.paypalOrderId || '—' }}</td>
                                <td class="text-center">
                                    <v-btn icon size="small" variant="text" color="grey-darken-1" @click="openReservationDialog(res)">
                                        <v-icon size="small">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon size="small" variant="text" color="red" @click="confirmDeleteReservation(res)">
                                        <v-icon size="small">mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>

                    <v-card v-else-if="!reservationsLoading" class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-calendar-blank-outline</v-icon>
                        <p class="text-body-1 text-grey">No reservations yet.</p>
                        <p class="text-body-2 text-grey-lighten-1">Reservations will appear here after guests book via PayPal.</p>
                    </v-card>

                    <div v-if="reservationsLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <!-- Reservation create/edit dialog -->
                    <v-dialog v-model="reservationDialog" max-width="600" persistent>
                        <v-card class="rounded-lg">
                            <v-card-title class="text-h6 pa-4">
                                {{ editingReservation.id ? 'Edit Reservation' : 'Add Reservation' }}
                            </v-card-title>
                            <v-divider />
                            <v-card-text class="pa-4">
                                <v-form v-model="reservationFormValid">
                                    <v-row dense>
                                        <v-col cols="6">
                                            <v-text-field
                                                v-model="editingReservation.checkIn"
                                                label="Check-in"
                                                type="date"
                                                :rules="[v => !!v || 'Check-in is required']"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-text-field
                                                v-model="editingReservation.checkOut"
                                                label="Check-out"
                                                type="date"
                                                :rules="[v => !!v || 'Check-out is required']"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="3">
                                            <v-text-field
                                                v-model.number="editingReservation.adults"
                                                label="Adults"
                                                type="number"
                                                min="1"
                                                :rules="[v => (v != null && v > 0) || 'At least 1 adult']"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="3">
                                            <v-text-field
                                                v-model.number="editingReservation.children"
                                                label="Children"
                                                type="number"
                                                min="0"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="3">
                                            <v-text-field
                                                v-model.number="editingReservation.infants"
                                                label="Infants"
                                                type="number"
                                                min="0"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="3">
                                            <v-text-field
                                                v-model.number="editingReservation.pets"
                                                label="Pets"
                                                type="number"
                                                min="0"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="4">
                                            <v-text-field
                                                v-model.number="editingReservation.nights"
                                                label="Nights"
                                                type="number"
                                                min="1"
                                                :rules="[v => (v != null && v > 0) || 'At least 1 night']"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="4">
                                            <v-text-field
                                                v-model.number="editingReservation.totalAmount"
                                                label="Total (cents)"
                                                type="number"
                                                min="0"
                                                :rules="[v => (v != null && v >= 0) || 'Amount required']"
                                                variant="outlined"
                                                density="comfortable"
                                                hint="In cents. 10000 = $100.00"
                                                persistent-hint
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="6">
                                            <v-text-field
                                                v-model="editingReservation.guestName"
                                                label="Guest Name"
                                                placeholder="e.g. John Doe"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-text-field
                                                v-model="editingReservation.guestEmail"
                                                label="Guest Email"
                                                placeholder="e.g. john@example.com"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense>
                                        <v-col cols="6">
                                            <v-select
                                                v-model="editingReservation.status"
                                                label="Status"
                                                :items="['COMPLETED', 'PENDING', 'CANCELLED', 'MANUAL']"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-text-field
                                                v-model="editingReservation.currency"
                                                label="Currency"
                                                variant="outlined"
                                                density="comfortable"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-text-field
                                        v-model="editingReservation.description"
                                        label="Description (optional)"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                </v-form>
                            </v-card-text>
                            <v-divider />
                            <v-card-actions class="pa-4">
                                <v-spacer />
                                <v-btn variant="text" @click="reservationDialog = false">Cancel</v-btn>
                                <v-btn
                                    color="primary"
                                    variant="flat"
                                    :disabled="!reservationFormValid"
                                    @click="saveReservation"
                                >
                                    {{ editingReservation.id ? 'Update' : 'Create' }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <!-- Delete confirmation dialog -->
                    <v-dialog v-model="reservationDeleteDialog" max-width="400">
                        <v-card class="rounded-lg">
                            <v-card-title class="text-h6 pa-4">Delete Reservation</v-card-title>
                            <v-card-text class="pa-4">
                                Are you sure you want to delete the reservation for
                                <strong>{{ reservationToDelete?.guestName || 'this guest' }}</strong>
                                ({{ reservationToDelete?.checkIn }} — {{ reservationToDelete?.checkOut }})?
                            </v-card-text>
                            <v-card-actions class="pa-4">
                                <v-spacer />
                                <v-btn variant="text" @click="reservationDeleteDialog = false">Cancel</v-btn>
                                <v-btn color="red" variant="flat" @click="deleteReservation">Delete</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </template>

                <!-- Settings Section -->
                <template v-if="activeSection === 'Settings'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Account Settings</h2>
                    </div>

                    <v-alert v-if="profileError" type="error" closable class="mb-4" @click:close="profileError = ''">
                        {{ profileError }}
                    </v-alert>
                    <v-alert v-if="profileSuccess" type="success" closable class="mb-4" @click:close="profileSuccess = ''">
                        {{ profileSuccess }}
                    </v-alert>

                    <div v-if="profileLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <v-card v-else class="rounded-lg pa-6" max-width="600" variant="outlined">
                        <v-form v-model="profileFormValid">
                            <v-text-field
                                v-model="profile.username"
                                label="Username"
                                prepend-inner-icon="mdi-account"
                                :rules="[v => !!v || 'Username is required']"
                                variant="outlined"
                                density="comfortable"
                                class="mb-3"
                            />
                            <v-text-field
                                v-model="profile.email"
                                label="Email"
                                prepend-inner-icon="mdi-email"
                                type="email"
                                :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Invalid email']"
                                variant="outlined"
                                density="comfortable"
                                class="mb-3"
                            />
                            <v-divider class="mb-4" />
                            <p class="text-body-2 text-grey-darken-1 mb-3">
                                <v-icon size="small" class="mr-1">mdi-lock-outline</v-icon>
                                Enter your current password to save any changes.
                            </p>
                            <v-text-field
                                v-model="profile.currentPassword"
                                label="Current Password"
                                prepend-inner-icon="mdi-lock"
                                :append-inner-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                :type="showCurrentPassword ? 'text' : 'password'"
                                @click:append-inner="showCurrentPassword = !showCurrentPassword"
                                :rules="[v => !!v || 'Current password is required']"
                                variant="outlined"
                                density="comfortable"
                                class="mb-3"
                            />
                            <v-text-field
                                v-model="profile.newPassword"
                                label="New Password (leave blank to keep current)"
                                prepend-inner-icon="mdi-lock-plus"
                                :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                :type="showNewPassword ? 'text' : 'password'"
                                @click:append-inner="showNewPassword = !showNewPassword"
                                :rules="profile.newPassword ? [v => v.length >= 6 || 'Password must be at least 6 characters'] : []"
                                variant="outlined"
                                density="comfortable"
                                class="mb-3"
                            />
                            <v-text-field
                                v-if="profile.newPassword"
                                v-model="profile.confirmPassword"
                                label="Confirm New Password"
                                prepend-inner-icon="mdi-lock-check"
                                :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                :type="showConfirmPassword ? 'text' : 'password'"
                                @click:append-inner="showConfirmPassword = !showConfirmPassword"
                                :rules="[v => v === profile.newPassword || 'Passwords do not match']"
                                variant="outlined"
                                density="comfortable"
                                class="mb-3"
                            />
                        </v-form>
                        <div class="d-flex justify-end mt-2">
                            <v-btn
                                color="primary"
                                variant="flat"
                                :disabled="!profileFormValid || profileSaving"
                                :loading="profileSaving"
                                prepend-icon="mdi-content-save"
                                @click="saveProfile"
                            >
                                Save Changes
                            </v-btn>
                        </div>
                    </v-card>
                </template>

                <!-- Properties Section -->
                <template v-if="activeSection === 'Properties'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Properties</h2>
                        <div class="flex-grow-1"></div>
                        <v-btn
                            color="primary"
                            prepend-icon="mdi-plus"
                            class="text-capitalize"
                            rounded="lg"
                            @click="openPropertyDialog()"
                        >
                            Add Property
                        </v-btn>
                    </div>

                    <v-alert v-if="propertyError" type="error" closable class="mb-4" @click:close="propertyError = ''">
                        {{ propertyError }}
                    </v-alert>

                    <v-table v-if="properties.length > 0" class="rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left">Name</th>
                                <th class="text-left">Slug</th>
                                <th class="text-center" style="width: 120px">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="prop in properties" :key="prop.id">
                                <td class="font-weight-medium">{{ prop.name }}</td>
                                <td class="text-grey-darken-1" style="font-family: monospace">{{ prop.slug }}</td>
                                <td class="text-center">
                                    <v-btn icon size="small" variant="text" color="grey-darken-1" @click="openPropertyDialog(prop)">
                                        <v-icon size="small">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn icon size="small" variant="text" color="red" @click="confirmDeleteProperty(prop)">
                                        <v-icon size="small">mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>

                    <v-card v-else-if="!propertiesLoading" class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-home-group</v-icon>
                        <p class="text-body-1 text-grey">No properties configured yet.</p>
                        <p class="text-body-2 text-grey-lighten-1">Click "Add Property" to create one.</p>
                    </v-card>

                    <div v-if="propertiesLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <!-- Property create/edit dialog -->
                    <v-dialog v-model="propertyDialog" max-width="500" persistent>
                        <v-card class="rounded-lg">
                            <v-card-title class="text-h6 pa-4">
                                {{ editingProperty.id ? 'Edit Property' : 'Add Property' }}
                            </v-card-title>
                            <v-divider />
                            <v-card-text class="pa-4">
                                <v-form v-model="propertyFormValid">
                                    <v-text-field
                                        v-model="editingProperty.name"
                                        label="Property Name"
                                        placeholder="e.g. Beach House"
                                        :rules="[v => !!v || 'Name is required']"
                                        variant="outlined"
                                        density="comfortable"
                                        class="mb-3"
                                    />
                                    <v-text-field
                                        v-model="editingProperty.slug"
                                        label="Slug"
                                        placeholder="e.g. beach-house"
                                        :rules="[v => !!v || 'Slug is required']"
                                        variant="outlined"
                                        density="comfortable"
                                        hint="URL-friendly identifier, must be unique"
                                        persistent-hint
                                    />
                                </v-form>
                            </v-card-text>
                            <v-divider />
                            <v-card-actions class="pa-4">
                                <v-spacer />
                                <v-btn variant="text" @click="propertyDialog = false">Cancel</v-btn>
                                <v-btn
                                    color="primary"
                                    variant="flat"
                                    :disabled="!propertyFormValid"
                                    @click="saveProperty"
                                >
                                    {{ editingProperty.id ? 'Update' : 'Create' }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <!-- Property delete confirmation dialog -->
                    <v-dialog v-model="propertyDeleteDialog" max-width="400">
                        <v-card class="rounded-lg">
                            <v-card-title class="text-h6 pa-4">Delete Property</v-card-title>
                            <v-card-text class="pa-4">
                                Are you sure you want to delete <strong>{{ propertyToDelete?.name }}</strong>?
                                This will not remove associated data, but it will be unlinked.
                            </v-card-text>
                            <v-card-actions class="pa-4">
                                <v-spacer />
                                <v-btn variant="text" @click="propertyDeleteDialog = false">Cancel</v-btn>
                                <v-btn color="red" variant="flat" @click="deleteProperty">Delete</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </template>

                <!-- Visitor Log Section -->
                <template v-if="activeSection === 'Visitor Log'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Visitor Log</h2>
                    </div>

                    <div class="d-flex align-center mb-4" style="gap: 12px;">
                        <v-text-field
                            v-model="visitorFromDate"
                            label="From"
                            type="date"
                            density="compact"
                            variant="outlined"
                            hide-details
                            style="max-width: 180px;"
                        ></v-text-field>
                        <v-text-field
                            v-model="visitorToDate"
                            label="To"
                            type="date"
                            density="compact"
                            variant="outlined"
                            hide-details
                            style="max-width: 180px;"
                        ></v-text-field>
                        <v-btn
                            color="black"
                            variant="outlined"
                            size="small"
                            class="text-capitalize"
                            @click="fetchVisitorLogs"
                            :loading="visitorLogsLoading"
                        >
                            Search
                        </v-btn>
                    </div>

                    <!-- Daily Summary -->
                    <v-table v-if="visitorSummary.length > 0" density="compact" class="mb-4 rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left font-weight-bold">Date</th>
                                <th class="text-left font-weight-bold">Visitors</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in visitorSummary" :key="row.visitDate">
                                <td>{{ row.visitDate }}</td>
                                <td>{{ row.visitCount }}</td>
                            </tr>
                        </tbody>
                    </v-table>

                    <!-- Detailed Logs -->
                    <v-table v-if="visitorLogs.length > 0" density="compact" class="rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left font-weight-bold">Date &amp; Time</th>
                                <th class="text-left font-weight-bold">IP Address</th>
                                <th class="text-left font-weight-bold">City</th>
                                <th class="text-left font-weight-bold">Region</th>
                                <th class="text-left font-weight-bold">Country</th>
                                <th class="text-left font-weight-bold">Page</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="log in visitorLogs" :key="log.id">
                                <td>{{ formatVisitTime(log.visitedAt) }}</td>
                                <td>{{ log.ipAddress }}</td>
                                <td>{{ log.city || '—' }}</td>
                                <td>{{ log.region || '—' }}</td>
                                <td>{{ log.country || '—' }}</td>
                                <td>{{ log.pageVisited || '/' }}</td>
                            </tr>
                        </tbody>
                    </v-table>

                    <div v-if="visitorLogsLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <v-card v-else-if="visitorLogs.length === 0 && visitorSummary.length === 0" class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-account-group-outline</v-icon>
                        <p class="text-body-1 text-grey">No visitor data for the selected date range.</p>
                    </v-card>
                </template>

                <!-- Configurations Section -->
                <template v-if="activeSection === 'Configurations'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Configurations</h2>
                    </div>

                    <v-alert v-if="configurationsError" type="error" closable class="mb-4" @click:close="configurationsError = ''">
                        {{ configurationsError }}
                    </v-alert>

                    <div v-if="configurationsLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <template v-else-if="configurations.length > 0">
                        <v-text-field
                            v-model="configSearch"
                            prepend-inner-icon="mdi-magnify"
                            label="Filter configurations..."
                            variant="outlined"
                            density="comfortable"
                            clearable
                            class="mb-4"
                            style="max-width: 400px"
                        />
                        <v-table class="rounded-lg elevation-1">
                            <thead>
                                <tr>
                                    <th class="text-left" style="width: 40%">Property</th>
                                    <th class="text-left">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="cfg in filteredConfigurations" :key="cfg.key">
                                    <td class="font-weight-medium text-body-2" style="font-family: monospace">{{ cfg.key }}</td>
                                    <td class="text-body-2" style="font-family: monospace; word-break: break-all">{{ cfg.value }}</td>
                                </tr>
                                <tr v-if="filteredConfigurations.length === 0">
                                    <td colspan="2" class="text-center text-grey pa-4">No matching configurations found.</td>
                                </tr>
                            </tbody>
                        </v-table>
                    </template>

                    <v-card v-else class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-cog-outline</v-icon>
                        <p class="text-body-1 text-grey">No configurations available.</p>
                    </v-card>
                </template>

                <!-- Hospitable Section -->
                <template v-if="activeSection === 'Hospitable'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Hospitable</h2>
                    </div>

                    <div class="d-flex align-center mb-4" style="gap: 12px;">
                        <v-text-field
                            v-model="hospitableStartDate"
                            label="Start Date"
                            type="date"
                            density="compact"
                            variant="outlined"
                            hide-details
                            style="max-width: 180px;"
                        ></v-text-field>
                        <v-text-field
                            v-model="hospitableEndDate"
                            label="End Date"
                            type="date"
                            density="compact"
                            variant="outlined"
                            hide-details
                            style="max-width: 180px;"
                        ></v-text-field>
                        <v-btn
                            color="black"
                            variant="outlined"
                            size="small"
                            class="text-capitalize"
                            @click="fetchHospitableReservations"
                            :loading="hospitableLoading"
                        >
                            Search
                        </v-btn>
                        <v-btn
                            color="green-darken-3"
                            variant="outlined"
                            size="small"
                            class="text-capitalize"
                            @click="exportHospitableToExcel"
                            :disabled="hospitableReservations.length === 0"
                            prepend-icon="mdi-microsoft-excel"
                        >
                            Export
                        </v-btn>
                    </div>

                    <v-alert v-if="hospitableError" type="error" closable class="mb-4" @click:close="hospitableError = ''">
                        {{ hospitableError }}
                    </v-alert>

                    <div v-if="hospitableLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <v-table v-else-if="hospitableReservations.length > 0" class="rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('guest_name')">Name <v-icon size="small" v-if="hospitableSortKey === 'guest_name'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('arrival_date')">Start Date <v-icon size="small" v-if="hospitableSortKey === 'arrival_date'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('departure_date')">End Date <v-icon size="small" v-if="hospitableSortKey === 'departure_date'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('total')">Num Days <v-icon size="small" v-if="hospitableSortKey === 'total'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('email')">Email <v-icon size="small" v-if="hospitableSortKey === 'email'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('phone')">Phone <v-icon size="small" v-if="hospitableSortKey === 'phone'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                                <th class="text-left font-weight-bold cursor-pointer" @click="sortHospitable('platform')">Platform <v-icon size="small" v-if="hospitableSortKey === 'platform'">{{ hospitableSortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="res in sortedHospitableReservations" :key="res.id">
                                <td>{{ res.guest ? (res.guest.first_name + ' ' + res.guest.last_name) : '—' }}</td>
                                <td>{{ res.arrival_date ? res.arrival_date.slice(0, 10) : '—' }}</td>
                                <td>{{ res.departure_date ? res.departure_date.slice(0, 10) : '—' }}</td>
                                <td>{{ res.guests ? res.guests.total : '—' }}</td>
                                <td>{{ res.guest && res.guest.email ? res.guest.email : '—' }}</td>
                                <td>{{ res.guest && res.guest.phone_numbers && res.guest.phone_numbers[0] ? res.guest.phone_numbers[0] : '—' }}</td>
                                <td>{{ res.platform || '—' }}</td>
                            </tr>
                        </tbody>
                    </v-table>

                    <v-card v-else class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-home-search-outline</v-icon>
                        <p class="text-body-1 text-grey">No reservations found. Use the date filters and click Search.</p>
                    </v-card>
                </template>

                <!-- Inquiries Section -->
                <template v-if="activeSection === 'Inquiries'">
                    <div class="d-flex align-center mb-4">
                        <h2 class="text-h5 font-weight-bold">Inquiries</h2>
                    </div>

                    <v-alert v-if="inquiryError" type="error" closable class="mb-4" @click:close="inquiryError = ''">
                        {{ inquiryError }}
                    </v-alert>

                    <div v-if="inquiriesLoading" class="text-center pa-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>

                    <v-table v-else-if="inquiries.length > 0" class="rounded-lg elevation-1">
                        <thead>
                            <tr>
                                <th class="text-left">Name</th>
                                <th class="text-left">Email</th>
                                <th class="text-left">Phone</th>
                                <th class="text-left">Property</th>
                                <th class="text-left">Message</th>
                                <th class="text-center">Newsletter</th>
                                <th class="text-left">Date</th>
                                <th class="text-center" style="width: 80px">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="inq in inquiries" :key="inq.id">
                                <td class="font-weight-medium">{{ inq.name }}</td>
                                <td>{{ inq.email }}</td>
                                <td>{{ inq.phone || '—' }}</td>
                                <td>{{ inq.propertyName }}</td>
                                <td style="max-width: 300px; white-space: pre-wrap;">{{ inq.message || '—' }}</td>
                                <td class="text-center">
                                    <v-icon :color="inq.newsletter ? 'green' : 'grey'" size="small">
                                        {{ inq.newsletter ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                    </v-icon>
                                </td>
                                <td>{{ formatDateTime(inq.createdAt) }}</td>
                                <td class="text-center">
                                    <v-btn icon size="small" variant="text" color="red" @click="confirmDeleteInquiry(inq)">
                                        <v-icon size="small">mdi-delete</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>

                    <v-card v-else class="pa-8 text-center rounded-lg" variant="outlined">
                        <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-email-open-outline</v-icon>
                        <p class="text-body-1 text-grey">No inquiries yet.</p>
                    </v-card>

                    <!-- Delete confirmation dialog -->
                    <v-dialog v-model="inquiryDeleteDialog" max-width="400">
                        <v-card class="rounded-lg">
                            <v-card-title class="text-h6 pa-4">Delete Inquiry</v-card-title>
                            <v-card-text class="pa-4">
                                Are you sure you want to delete the inquiry from
                                <strong>{{ inquiryToDelete?.name }}</strong>
                                ({{ inquiryToDelete?.email }})?
                            </v-card-text>
                            <v-card-actions class="pa-4">
                                <v-spacer />
                                <v-btn variant="text" @click="inquiryDeleteDialog = false">Cancel</v-btn>
                                <v-btn color="red" variant="flat" @click="deleteInquiry">Delete</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </template>

                <!-- Default / other sections -->
                <template v-else>
                    <v-row align="center" justify="center" class="fill-height">
                        <v-col cols="auto" class="text-center">
                            <v-icon size="64" color="grey-lighten-1">mdi-view-dashboard-outline</v-icon>
                            <p class="text-h6 text-grey mt-4">Select a section from the menu</p>
                        </v-col>
                    </v-row>
                </template>
            </v-container>
        </v-main>

        <!-- Lightbox dialog -->
        <v-dialog v-model="lightboxOpen" max-width="900" content-class="lightbox-dialog">
            <v-card color="black" flat>
                <v-btn icon variant="text" color="white" style="position:absolute;top:8px;left:8px;z-index:10" @click="lightboxOpen = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <div class="d-flex align-center justify-center" style="min-height:500px;position:relative">
                    <v-btn icon variant="text" color="white" style="position:absolute;left:8px;top:50%;transform:translateY(-50%);z-index:10" @click="prevImage" :disabled="lightboxIndex === 0">
                        <v-icon size="large">mdi-chevron-left</v-icon>
                    </v-btn>
                    <img :src="images[lightboxIndex]" alt="Gallery image" style="max-width:100%;max-height:500px;object-fit:contain" />
                    <v-btn icon variant="text" color="white" style="position:absolute;right:8px;top:50%;transform:translateY(-50%);z-index:10" @click="nextImage" :disabled="lightboxIndex === images.length - 1">
                        <v-icon size="large">mdi-chevron-right</v-icon>
                    </v-btn>
                </div>
                <div class="text-center text-white text-body-2 pb-3">
                    {{ lightboxIndex + 1 }} / {{ images.length }}
                </div>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script>

    import { getCookie, deleteCookie } from '@/utils/cookie'
    import { sessionDestroy } from '@/utils/session'
    import api from './backend-api'
    import { useDisplay } from 'vuetify'
    import * as XLSX from 'xlsx'

    // Reuse the same image loader from Hillwood
    function loadAssetImages() {
        const context = require.context('./hillwood', false, /\.(jpe?g|png|gif|webp|svg)$/i)
        return context.keys().map(key => ({
            filename: key.replace('./', ''),
            src: context(key)
        }))
    }

    export default {
        props: {
            source: String,
        },
        setup() {
            const { lgAndUp } = useDisplay();
            return { lgAndUp };
        },
        data: () => ({
            dialog: false,
            drawer: null,
            activeSection: 'Images',
            // Image gallery data
            assetImages: loadAssetImages(),
            imageSortOrder: [],
            sortMode: false,
            dragIndex: null,
            lightboxOpen: false,
            lightboxIndex: 0,
            // Fees data
            fees: [],
            feesLoading: false,
            feesSaving: false,
            feeError: '',
            feeSuccess: '',
            feeFields: [
                { key: 'Booking surcharge', label: 'Booking surcharge', type: 'percentage', value: null, id: null },
                { key: 'Cleaning', label: 'Cleaning', type: 'amount', value: null, id: null },
                { key: 'Credit card surcharge', label: 'Credit card surcharge', type: 'percentage', value: null, id: null },
                { key: 'Occupancy tax', label: 'Occupancy tax', type: 'percentage', value: null, id: null },
                { key: 'Pet fee', label: 'Pet fee', type: 'amount', value: null, id: null },
            ],
            // Settings / Profile data
            profileLoading: false,
            profileSaving: false,
            profileSuccess: '',
            profileError: '',
            profileFormValid: false,
            profile: {
                username: '',
                email: '',
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            },
            showCurrentPassword: false,
            showNewPassword: false,
            showConfirmPassword: false,
            // Configurations data
            configurations: [],
            configurationsLoading: false,
            configurationsError: '',
            configSearch: '',
            // Reservations CRUD data
            reservations: [],
            reservationsLoading: false,
            reservationDialog: false,
            reservationDeleteDialog: false,
            reservationToDelete: null,
            reservationFormValid: false,
            reservationError: '',
            editingReservation: {
                id: null,
                checkIn: '',
                checkOut: '',
                adults: 1,
                children: 0,
                infants: 0,
                pets: 0,
                nights: 1,
                totalAmount: 0,
                currency: 'USD',
                status: 'MANUAL',
                guestName: '',
                guestEmail: '',
                description: ''
            },
            // Visitor log data
            visitorLogs: [],
            visitorSummary: [],
            visitorLogsLoading: false,
            visitorFromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
            visitorToDate: new Date().toISOString().slice(0, 10),
            // Properties data
            properties: [],
            selectedPropertyId: null,
            propertiesLoading: false,
            propertyDialog: false,
            propertyDeleteDialog: false,
            propertyToDelete: null,
            propertyFormValid: false,
            propertyError: '',
            editingProperty: {
                id: null,
                name: '',
                slug: ''
            },
            // Discounts data
            discounts: [],
            discountsLoading: false,
            discountDialog: false,
            discountDeleteDialog: false,
            discountToDelete: null,
            discountFormValid: false,
            discountError: '',
            discountSuccess: '',
            editingDiscount: {
                id: null,
                type: 'LENGTH_OF_STAY',
                thresholdDays: null,
                percentage: null,
                description: '',
                active: true
            },
            discountTypes: [
                { title: 'Length of Stay', value: 'LENGTH_OF_STAY' },
                { title: 'Last Minute', value: 'LAST_MINUTE' }
            ],
            // Hospitable data
            hospitableReservations: [],
            hospitableLoading: false,
            hospitableError: '',
            hospitableStartDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
            hospitableEndDate: new Date(new Date().getFullYear(), 11, 31).toISOString().slice(0, 10),
            hospitableSortKey: 'arrival_date',
            hospitableSortAsc: true,
            // Inquiries data
            inquiries: [],
            inquiriesLoading: false,
            inquiryError: '',
            inquiryDeleteDialog: false,
            inquiryToDelete: null,
            items: [
                { icon: 'mdi-home-group', text: 'Properties' },
                { icon: 'mdi-image-multiple', text: 'Images' },
                { icon: 'mdi-currency-usd', text: 'Fees' },
                { icon: 'mdi-percent-outline', text: 'Discounts' },
                { icon: 'mdi-calendar-check', text: 'Reservations' },
                { icon: 'mdi-email-outline', text: 'Inquiries' },
                { icon: 'mdi-home-analytics', text: 'Hospitable' },
                { icon: 'mdi-account-eye', text: 'Visitor Log' },
                { icon: 'mdi-tune', text: 'Configurations' },
                { icon: 'mdi-cog', text: 'Settings' },
            ],
        }),
        watch: {
            activeSection(newVal) {
                if (newVal === 'Fees') {
                    this.fetchFees()
                }
                if (newVal === 'Reservations') {
                    this.fetchReservations()
                }
                if (newVal === 'Settings') {
                    this.fetchProfile()
                }
                if (newVal === 'Configurations') {
                    this.fetchConfigurations()
                }
                if (newVal === 'Properties') {
                    this.fetchProperties()
                }
                if (newVal === 'Visitor Log') {
                    this.fetchVisitorLogs()
                }
                if (newVal === 'Discounts') {
                    this.fetchDiscounts()
                }
                if (newVal === 'Inquiries') {
                    this.fetchInquiries()
                }
            }
        },
        mounted() {
            this.fetchProperties()
            this.fetchImageSortOrder()
        },
        computed: {
            filteredConfigurations() {
                if (!this.configSearch) return this.configurations
                const search = this.configSearch.toLowerCase()
                return this.configurations.filter(cfg =>
                    cfg.key.toLowerCase().includes(search) || cfg.value.toLowerCase().includes(search)
                )
            },
            images() {
                const assets = this.assetImages
                if (this.imageSortOrder.length > 0) {
                    const orderMap = {}
                    this.imageSortOrder.forEach((filename, index) => {
                        orderMap[filename] = index
                    })
                    const sorted = [...assets].sort((a, b) => {
                        const posA = orderMap[a.filename] !== undefined ? orderMap[a.filename] : 9999
                        const posB = orderMap[b.filename] !== undefined ? orderMap[b.filename] : 9999
                        return posA - posB
                    })
                    return sorted.map(img => img.src)
                }
                return assets.map(img => img.src)
            },
            sortedAssetItems() {
                const assets = this.assetImages
                if (this.imageSortOrder.length > 0) {
                    const orderMap = {}
                    this.imageSortOrder.forEach((filename, index) => {
                        orderMap[filename] = index
                    })
                    return [...assets].sort((a, b) => {
                        const posA = orderMap[a.filename] !== undefined ? orderMap[a.filename] : 9999
                        const posB = orderMap[b.filename] !== undefined ? orderMap[b.filename] : 9999
                        return posA - posB
                    })
                }
                return assets
            },
            sortedHospitableReservations() {
                const list = [...this.hospitableReservations]
                const key = this.hospitableSortKey
                const asc = this.hospitableSortAsc
                list.sort((a, b) => {
                    let valA, valB
                    if (key === 'guest_name') {
                        valA = a.guest ? (a.guest.first_name + ' ' + a.guest.last_name).toLowerCase() : ''
                        valB = b.guest ? (b.guest.first_name + ' ' + b.guest.last_name).toLowerCase() : ''
                    } else if (key === 'total') {
                        valA = a.guests ? a.guests.total : 0
                        valB = b.guests ? b.guests.total : 0
                    } else if (key === 'email') {
                        valA = (a.guest && a.guest.email) ? a.guest.email.toLowerCase() : ''
                        valB = (b.guest && b.guest.email) ? b.guest.email.toLowerCase() : ''
                    } else if (key === 'phone') {
                        valA = (a.guest && a.guest.phone_numbers && a.guest.phone_numbers[0]) ? a.guest.phone_numbers[0] : ''
                        valB = (b.guest && b.guest.phone_numbers && b.guest.phone_numbers[0]) ? b.guest.phone_numbers[0] : ''
                    } else if (key === 'platform') {
                        valA = (a.platform || '').toLowerCase()
                        valB = (b.platform || '').toLowerCase()
                    } else {
                        valA = a[key] || ''
                        valB = b[key] || ''
                    }
                    if (valA < valB) return asc ? -1 : 1
                    if (valA > valB) return asc ? 1 : -1
                    return 0
                })
                return list
            }
        },
        methods: {
            fetchImageSortOrder() {
                api.getImageSortOrder('hillwood', this.selectedPropertyId)
                    .then(response => {
                        this.imageSortOrder = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load image sort order:', error)
                        this.imageSortOrder = []
                    })
            },
            saveImageSortOrder() {
                const filenames = this.sortedAssetItems.map(img => img.filename)
                api.saveImageSortOrder('hillwood', filenames, this.selectedPropertyId)
                    .then(() => {
                        console.log('Image sort order saved')
                    })
                    .catch(error => {
                        console.error('Failed to save image sort order:', error)
                    })
            },
            onDragStart(index) {
                this.dragIndex = index
            },
            onDragOver(event) {
                event.preventDefault()
            },
            onDrop(targetIndex) {
                if (this.dragIndex === null || this.dragIndex === targetIndex) return
                const items = [...this.sortedAssetItems]
                const [moved] = items.splice(this.dragIndex, 1)
                items.splice(targetIndex, 0, moved)
                this.imageSortOrder = items.map(img => img.filename)
                this.dragIndex = null
            },
            onDragEnd() {
                this.dragIndex = null
            },
            toggleSortMode() {
                if (this.sortMode) {
                    this.saveImageSortOrder()
                }
                this.sortMode = !this.sortMode
            },
            openLightbox(index) {
                this.lightboxIndex = index
                this.lightboxOpen = true
            },
            nextImage() {
                if (this.lightboxIndex < this.images.length - 1) {
                    this.lightboxIndex++
                }
            },
            prevImage() {
                if (this.lightboxIndex > 0) {
                    this.lightboxIndex--
                }
            },
            // Fees methods
            fetchFees() {
                this.feesLoading = true
                this.feeError = ''
                api.getFees(this.selectedPropertyId)
                    .then(response => {
                        this.fees = response.data
                        // Populate feeFields from fetched fees
                        for (const field of this.feeFields) {
                            const match = this.fees.find(f => f.name === field.key)
                            if (match) {
                                field.id = match.id
                                field.type = match.amount != null ? 'amount' : 'percentage'
                                field.value = field.type === 'amount'
                                    ? (match.amount != null ? match.amount / 100 : null)
                                    : (match.percentage != null ? parseFloat(match.percentage) : null)
                            } else {
                                field.id = null
                                field.value = null
                            }
                        }
                    })
                    .catch(error => {
                        console.error('Failed to load fees:', error)
                        this.feeError = 'Failed to load fees.'
                    })
                    .finally(() => {
                        this.feesLoading = false
                    })
            },
            saveFees() {
                this.feesSaving = true
                this.feeError = ''
                this.feeSuccess = ''
                const promises = this.feeFields.map(field => {
                    const hasValue = field.value != null && field.value !== '' && field.value > 0
                    if (hasValue) {
                        const payload = {
                            name: field.key,
                            description: null
                        }
                        if (field.type === 'amount') {
                            payload.amount = Math.round(field.value * 100)
                            payload.percentage = null
                        } else {
                            payload.amount = null
                            payload.percentage = field.value
                        }
                        if (field.id) {
                            return api.updateFee(field.id, payload, this.selectedPropertyId)
                        } else {
                            return api.createFee(payload, this.selectedPropertyId)
                        }
                    } else if (field.id) {
                        // Value cleared — delete the existing fee
                        return api.deleteFee(field.id)
                    }
                    return Promise.resolve()
                })
                Promise.all(promises)
                    .then(() => {
                        this.feeSuccess = 'Fees saved successfully.'
                        this.fetchFees()
                    })
                    .catch(error => {
                        const msg = error.response?.data?.error || 'Failed to save fees.'
                        this.feeError = msg
                    })
                    .finally(() => {
                        this.feesSaving = false
                    })
            },
            formatCents(cents) {
                return '$' + (cents / 100).toFixed(2)
            },
            getStatusColor(status) {
                switch (status) {
                    case 'COMPLETED': return 'green'
                    case 'PENDING': return 'orange'
                    case 'CANCELLED': return 'red'
                    case 'MANUAL': return 'blue'
                    default: return 'grey'
                }
            },
            // Discount methods
            fetchDiscounts() {
                this.discountsLoading = true
                this.discountError = ''
                api.getDiscounts(this.selectedPropertyId)
                    .then(response => {
                        this.discounts = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load discounts:', error)
                        this.discountError = 'Failed to load discounts.'
                    })
                    .finally(() => {
                        this.discountsLoading = false
                    })
            },
            openDiscountDialog(discount = null) {
                if (discount) {
                    this.editingDiscount = {
                        id: discount.id,
                        type: discount.type,
                        thresholdDays: discount.thresholdDays,
                        percentage: discount.percentage,
                        description: discount.description || '',
                        active: discount.active
                    }
                } else {
                    this.editingDiscount = {
                        id: null,
                        type: 'LENGTH_OF_STAY',
                        thresholdDays: null,
                        percentage: null,
                        description: '',
                        active: true
                    }
                }
                this.discountDialog = true
            },
            saveDiscount() {
                const payload = {
                    type: this.editingDiscount.type,
                    thresholdDays: this.editingDiscount.thresholdDays,
                    percentage: this.editingDiscount.percentage,
                    description: this.editingDiscount.description,
                    active: this.editingDiscount.active
                }

                const promise = this.editingDiscount.id
                    ? api.updateDiscount(this.editingDiscount.id, payload, this.selectedPropertyId)
                    : api.createDiscount(payload, this.selectedPropertyId)

                promise
                    .then(() => {
                        this.discountDialog = false
                        this.discountSuccess = 'Discount saved successfully.'
                        this.fetchDiscounts()
                    })
                    .catch(error => {
                        const msg = error.response?.data?.error || 'Failed to save discount.'
                        this.discountError = msg
                    })
            },
            confirmDeleteDiscount(discount) {
                this.discountToDelete = discount
                this.discountDeleteDialog = true
            },
            deleteDiscount() {
                if (!this.discountToDelete) return
                api.deleteDiscount(this.discountToDelete.id)
                    .then(() => {
                        this.discountDeleteDialog = false
                        this.discountToDelete = null
                        this.fetchDiscounts()
                    })
                    .catch(error => {
                        console.error('Failed to delete discount:', error)
                        this.discountError = 'Failed to delete discount.'
                        this.discountDeleteDialog = false
                    })
            },
            // Reservations methods
            fetchReservations() {
                this.reservationsLoading = true
                this.reservationError = ''
                api.getReservations(this.selectedPropertyId)
                    .then(response => {
                        this.reservations = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load reservations:', error)
                        this.reservationError = 'Failed to load reservations.'
                    })
                    .finally(() => {
                        this.reservationsLoading = false
                    })
            },
            openReservationDialog(reservation = null) {
                if (reservation) {
                    this.editingReservation = {
                        id: reservation.id,
                        checkIn: reservation.checkIn || '',
                        checkOut: reservation.checkOut || '',
                        adults: reservation.adults || 1,
                        children: reservation.children || 0,
                        infants: reservation.infants || 0,
                        pets: reservation.pets || 0,
                        nights: reservation.nights || 1,
                        totalAmount: reservation.totalAmount || 0,
                        currency: reservation.currency || 'USD',
                        status: reservation.status || 'MANUAL',
                        guestName: reservation.guestName || '',
                        guestEmail: reservation.guestEmail || '',
                        description: reservation.description || ''
                    }
                } else {
                    this.editingReservation = {
                        id: null,
                        checkIn: '',
                        checkOut: '',
                        adults: 1,
                        children: 0,
                        infants: 0,
                        pets: 0,
                        nights: 1,
                        totalAmount: 0,
                        currency: 'USD',
                        status: 'MANUAL',
                        guestName: '',
                        guestEmail: '',
                        description: ''
                    }
                }
                this.reservationDialog = true
            },
            saveReservation() {
                const payload = {
                    checkIn: this.editingReservation.checkIn,
                    checkOut: this.editingReservation.checkOut,
                    adults: this.editingReservation.adults,
                    children: this.editingReservation.children,
                    infants: this.editingReservation.infants,
                    pets: this.editingReservation.pets,
                    nights: this.editingReservation.nights,
                    totalAmount: this.editingReservation.totalAmount,
                    currency: this.editingReservation.currency,
                    status: this.editingReservation.status,
                    guestName: this.editingReservation.guestName || null,
                    guestEmail: this.editingReservation.guestEmail || null,
                    description: this.editingReservation.description || null
                }

                const promise = this.editingReservation.id
                    ? api.updateReservation(this.editingReservation.id, payload)
                    : api.createReservation(payload)

                promise
                    .then(() => {
                        this.reservationDialog = false
                        this.fetchReservations()
                    })
                    .catch(error => {
                        const msg = error.response?.data?.error || 'Failed to save reservation.'
                        this.reservationError = msg
                    })
            },
            confirmDeleteReservation(reservation) {
                this.reservationToDelete = reservation
                this.reservationDeleteDialog = true
            },
            deleteReservation() {
                if (!this.reservationToDelete) return
                api.deleteReservation(this.reservationToDelete.id)
                    .then(() => {
                        this.reservationDeleteDialog = false
                        this.reservationToDelete = null
                        this.fetchReservations()
                    })
                    .catch(error => {
                        console.error('Failed to delete reservation:', error)
                        this.reservationError = 'Failed to delete reservation.'
                        this.reservationDeleteDialog = false
                    })
            },
            // Profile / Settings methods
            fetchProfile() {
                const token = getCookie('token') || sessionStorage.getItem('token')
                if (!token) {
                    this.profileError = 'No active session. Please log in again.'
                    return
                }
                this.profileLoading = true
                this.profileError = ''
                this.profileSuccess = ''
                api.getProfile(token)
                    .then(response => {
                        this.profile.username = response.data.username
                        this.profile.email = response.data.email
                        this.profile.currentPassword = ''
                        this.profile.newPassword = ''
                        this.profile.confirmPassword = ''
                    })
                    .catch(error => {
                        console.error('Failed to load profile:', error)
                        this.profileError = 'Failed to load profile.'
                    })
                    .finally(() => {
                        this.profileLoading = false
                    })
            },
            saveProfile() {
                const token = getCookie('token') || sessionStorage.getItem('token')
                if (!token) {
                    this.profileError = 'No active session. Please log in again.'
                    return
                }
                if (this.profile.newPassword && this.profile.newPassword !== this.profile.confirmPassword) {
                    this.profileError = 'Passwords do not match.'
                    return
                }
                this.profileSaving = true
                this.profileError = ''
                this.profileSuccess = ''
                const payload = {
                    username: this.profile.username,
                    email: this.profile.email,
                    currentPassword: this.profile.currentPassword
                }
                if (this.profile.newPassword) {
                    payload.newPassword = this.profile.newPassword
                }
                api.updateProfile(token, payload)
                    .then(response => {
                        this.profileSuccess = response.data.message || 'Profile updated successfully'
                        this.profile.username = response.data.username
                        this.profile.email = response.data.email
                        this.profile.currentPassword = ''
                        this.profile.newPassword = ''
                        this.profile.confirmPassword = ''
                    })
                    .catch(error => {
                        const msg = error.response?.data?.message || 'Failed to update profile.'
                        this.profileError = msg
                    })
                    .finally(() => {
                        this.profileSaving = false
                    })
            },
            // Configurations methods
            fetchConfigurations() {
                this.configurationsLoading = true
                this.configurationsError = ''
                api.getConfigurations()
                    .then(response => {
                        this.configurations = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load configurations:', error)
                        this.configurationsError = 'Failed to load configurations.'
                    })
                    .finally(() => {
                        this.configurationsLoading = false
                    })
            },
            // Property methods
            onPropertyChanged() {
                if (this.activeSection === 'Images') this.fetchImageSortOrder()
                if (this.activeSection === 'Fees') this.fetchFees()
                if (this.activeSection === 'Reservations') this.fetchReservations()
                if (this.activeSection === 'Discounts') this.fetchDiscounts()
            },
            fetchProperties() {
                this.propertiesLoading = true
                this.propertyError = ''
                api.getProperties()
                    .then(response => {
                        this.properties = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load properties:', error)
                        this.propertyError = 'Failed to load properties.'
                    })
                    .finally(() => {
                        this.propertiesLoading = false
                    })
            },
            openPropertyDialog(property = null) {
                if (property) {
                    this.editingProperty = {
                        id: property.id,
                        name: property.name,
                        slug: property.slug
                    }
                } else {
                    this.editingProperty = {
                        id: null,
                        name: '',
                        slug: ''
                    }
                }
                this.propertyDialog = true
            },
            saveProperty() {
                const payload = {
                    name: this.editingProperty.name,
                    slug: this.editingProperty.slug
                }
                const promise = this.editingProperty.id
                    ? api.updateProperty(this.editingProperty.id, payload)
                    : api.createProperty(payload)

                promise
                    .then(() => {
                        this.propertyDialog = false
                        this.fetchProperties()
                    })
                    .catch(error => {
                        const msg = error.response?.data?.error || 'Failed to save property.'
                        this.propertyError = msg
                    })
            },
            confirmDeleteProperty(property) {
                this.propertyToDelete = property
                this.propertyDeleteDialog = true
            },
            deleteProperty() {
                if (!this.propertyToDelete) return
                api.deleteProperty(this.propertyToDelete.id)
                    .then(() => {
                        this.propertyDeleteDialog = false
                        this.propertyToDelete = null
                        this.fetchProperties()
                        if (this.selectedPropertyId === this.propertyToDelete?.id) {
                            this.selectedPropertyId = null
                        }
                    })
                    .catch(error => {
                        console.error('Failed to delete property:', error)
                        this.propertyError = 'Failed to delete property.'
                        this.propertyDeleteDialog = false
                    })
            },
            // Visitor log methods
            fetchVisitorLogs() {
                this.visitorLogsLoading = true
                const from = this.visitorFromDate
                const to = this.visitorToDate
                Promise.all([
                    api.getVisitorSummary(from, to),
                    api.getVisitorLogs(from, to)
                ])
                    .then(([summaryResp, logsResp]) => {
                        this.visitorSummary = summaryResp.data
                        this.visitorLogs = logsResp.data
                    })
                    .catch(err => {
                        console.error('Failed to load visitor data:', err)
                        this.visitorSummary = []
                        this.visitorLogs = []
                    })
                    .finally(() => {
                        this.visitorLogsLoading = false
                    })
            },
            formatVisitTime(dateTimeStr) {
                if (!dateTimeStr) return ''
                const d = new Date(dateTimeStr)
                return d.toLocaleString()
            },
            // Hospitable methods
            fetchHospitableReservations() {
                this.hospitableLoading = true
                this.hospitableError = ''
                api.getHospitableReservations(this.hospitableStartDate, this.hospitableEndDate)
                    .then(response => {
                        this.hospitableReservations = response.data
                    })
                    .catch(error => {
                        console.error('Failed to load hospitable reservations:', error)
                        this.hospitableError = 'Failed to load reservations from Hospitable.'
                        this.hospitableReservations = []
                    })
                    .finally(() => {
                        this.hospitableLoading = false
                    })
            },
            sortHospitable(key) {
                if (this.hospitableSortKey === key) {
                    this.hospitableSortAsc = !this.hospitableSortAsc
                } else {
                    this.hospitableSortKey = key
                    this.hospitableSortAsc = true
                }
            },
            exportHospitableToExcel() {
                const rows = this.sortedHospitableReservations.map(res => ({
                    'Name': res.guest ? (res.guest.first_name + ' ' + res.guest.last_name) : '',
                    'Start Date': res.arrival_date ? res.arrival_date.slice(0, 10) : '',
                    'End Date': res.departure_date ? res.departure_date.slice(0, 10) : '',
                    'Total': res.guests ? res.guests.total : '',
                    'Email': res.guest && res.guest.email ? res.guest.email : '',
                    'Phone': res.guest && res.guest.phone_numbers && res.guest.phone_numbers[0] ? res.guest.phone_numbers[0] : '',
                    'Platform': res.platform || ''
                }))
                const ws = XLSX.utils.json_to_sheet(rows)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, 'Reservations')
                XLSX.writeFile(wb, `hospitable-reservations-${this.hospitableStartDate}-to-${this.hospitableEndDate}.xlsx`)
            },
            // Inquiry methods
            fetchInquiries() {
                this.inquiriesLoading = true
                this.inquiryError = ''
                api.getInquiries()
                    .then(response => {
                        this.inquiries = response.data
                    })
                    .catch(err => {
                        this.inquiryError = err.response?.data?.message || 'Failed to load inquiries'
                    })
                    .finally(() => {
                        this.inquiriesLoading = false
                    })
            },
            confirmDeleteInquiry(inquiry) {
                this.inquiryToDelete = inquiry
                this.inquiryDeleteDialog = true
            },
            deleteInquiry() {
                if (!this.inquiryToDelete) return
                api.deleteInquiry(this.inquiryToDelete.id)
                    .then(() => {
                        this.inquiries = this.inquiries.filter(i => i.id !== this.inquiryToDelete.id)
                        this.inquiryDeleteDialog = false
                        this.inquiryToDelete = null
                    })
                    .catch(err => {
                        this.inquiryError = err.response?.data?.message || 'Failed to delete inquiry'
                        this.inquiryDeleteDialog = false
                    })
            },
            formatDateTime(dateTimeStr) {
                if (!dateTimeStr) return ''
                const d = new Date(dateTimeStr)
                return d.toLocaleString()
            },
            logout() {
                let deviceId = getCookie('deviceId');
                sessionDestroy();
                deleteCookie('token');
                if(deviceId != null) api.deleteTokenByDeviceId(deviceId);
                this.$router.push('/').catch(error => {console.log(error);})
            }
        }
    }
</script>

<style scoped>
.sort-item {
    position: relative;
    cursor: grab;
    border: 2px solid transparent;
    border-radius: 12px;
    padding: 4px;
    transition: border-color 0.2s, opacity 0.2s, box-shadow 0.2s;
}

.sort-item:hover {
    border-color: #222;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.sort-item-dragging {
    opacity: 0.4;
    border-color: #E31C5F;
}

.sort-handle {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 5;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    padding: 4px 10px;
    backdrop-filter: blur(4px);
}

.image-card {
    transition: transform 0.2s, box-shadow 0.2s;
}

.image-card:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.cursor-pointer {
    cursor: pointer;
}
</style>